import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { collection, getDocs, limit, doc, getDoc } from 'firebase/firestore';

export default function DiagnosticPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [diagnostics, setDiagnostics] = useState({});
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState('');

  const runDiagnostics = useCallback(async () => {
    setLoading(true);
    const results = {};

    try {
      // Test 1: Basic Firebase connection
      results.firebase = '✅ Connected';
    } catch (error) {
      results.firebase = `❌ Error: ${error.message}`;
    }

    try {
      // Test 2: Firestore connection
      await getDocs(collection(db, 'users'), limit(1));
      results.firestore = '✅ Connected - Can read data';
    } catch (error) {
      results.firestore = `❌ Error: ${error.message}`;
    }

    try {
      // Test 3: Network connectivity
      await fetch('https://firestore.googleapis.com', { 
        method: 'HEAD',
        mode: 'no-cors'
      });
      results.network = '✅ Can reach Firestore servers';
    } catch (error) {
      results.network = `❌ Error: ${error.message}`;
    }

    // Test 4: User authentication
    if (currentUser) {
      results.auth = `✅ User authenticated: ${currentUser.email}`;
      
      // Test 5: User profile in Firestore
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          results.userProfile = '✅ User profile exists in Firestore';
        } else {
          results.userProfile = '⚠️ User profile not found in Firestore';
        }
      } catch (error) {
        results.userProfile = `❌ Error checking profile: ${error.message}`;
      }
    } else {
      results.auth = '❌ No user authenticated';
      results.userProfile = 'N/A - No user';
    }

    setDiagnostics(results);
    setLoading(false);
  }, [currentUser]);

  const testProfileUpdate = async () => {
    if (!currentUser) {
      setTestResult('❌ No user authenticated');
      return;
    }

    try {
      setTestResult('Testing profile update...');
      
      // Test updating a simple field
      const testUpdate = { bio: 'Test update at ' + new Date().toLocaleTimeString() };
      
      // This would normally go through AuthContext, but we're testing directly
      const userDocRef = doc(db, 'users', currentUser.uid);
      await userDocRef.update(testUpdate);
      
      setTestResult('✅ Profile update test successful!');
    } catch (error) {
      setTestResult(`❌ Profile update test failed: ${error.message}`);
    }
  };

  useEffect(() => {
    runDiagnostics();
  }, [runDiagnostics]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        🔧 System Diagnostics - Feature 3 (Simplified)
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Connection Status
        </Typography>
        
        <List>
          {Object.entries(diagnostics).map(([key, value]) => (
            <React.Fragment key={key}>
              <ListItem>
                <ListItemText 
                  primary={key.charAt(0).toUpperCase() + key.slice(1)} 
                  secondary={value}
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>

        <Box sx={{ mt: 2 }}>
          <Button 
            variant="contained" 
            onClick={runDiagnostics}
            disabled={loading}
          >
            {loading ? 'Running...' : 'Run Diagnostics'}
          </Button>
        </Box>
      </Paper>

      {/* Profile Testing */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Profile Management Tests
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            onClick={testProfileUpdate}
            disabled={!currentUser}
          >
            Test Profile Update
          </Button>
          
          <Button 
            variant="outlined" 
            onClick={() => navigate('/profile')}
          >
            Go to Profile Page
          </Button>
        </Box>

        {testResult && (
          <Alert severity={testResult.includes('✅') ? 'success' : 'error'} sx={{ mb: 2 }}>
            {testResult}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Common Solutions
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Firestore Connection Issues:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            1. Check if Firestore is enabled in Firebase Console
            2. Verify security rules allow read/write access
            3. Check network/firewall settings
            4. Ensure Firebase project is properly configured
          </Typography>
        </Alert>

        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Profile Pictures:</strong> Now using simple email initials - no storage required!
          </Typography>
        </Alert>

        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Current Status:</strong> The app will work with local data storage even if Firestore is unavailable.
          </Typography>
        </Alert>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
        <Button variant="outlined" onClick={() => navigate('/test')}>
          Test Page
        </Button>
      </Box>
    </Container>
  );
}
